import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { twMerge } from "tailwind-merge";

import { ConfirmActionModal } from "~/common/components";
import { CancelSubmitButtons, ErrorText } from "~/common/components/ui";
import Input from "~/common/components/ui/Input";
import {
  SCHEDULING_METHOD_VARIANT_LABELS,
  SCHEDULING_METHOD_VARIANTS,
} from "~/common/constants/schedulingMethods";
import { schedulingMethodSchema } from "~/common/schemas/schedulingMethod";
import { SchedulingMethod } from "~/common/types/schedulingMethods";
import { formatPhoneNumber, formatPhoneNumberField } from "~/common/utils";
import { useCurrentTeamMemberContext } from "~/providers/hooks";
import { useUpdateSchedulingMethods } from "~/providers/hooks/useUpdateSchedulingMethods";

export const SchedulingMethodsForm = ({
  defaultValues,
  onCancel,
  onError,
  onSuccess,
}: {
  defaultValues?: SchedulingMethod;
  onCancel?: () => void;
  onError?: (error: Error) => void;
  onSuccess?: () => void;
}) => {
  const [confirm, setConfirm] = useState<boolean>(false);
  const teamMember = useCurrentTeamMemberContext();

  const {
    handleSubmit,
    register,
    formState: { errors, isDirty },
    watch,
  } = useForm<SchedulingMethod>({
    resolver: zodResolver(schedulingMethodSchema),
    mode: "onTouched",
    defaultValues: {
      phone_number: {
        number: formatPhoneNumber(defaultValues?.phone_number?.number ?? ""),
        ext: defaultValues?.phone_number?.ext ?? "",
        variant: defaultValues?.phone_number?.variant ?? "",
      },
      email: defaultValues?.email ?? "",
      booking_link: defaultValues?.booking_link ?? "",
    },
  });

  const { mutate, isPending } = useUpdateSchedulingMethods();

  const submitHandler: SubmitHandler<SchedulingMethod> = (data) => {
    const payload = { ...data };

    if (!payload.phone_number?.number) {
      delete payload.phone_number;
    }

    if (!payload.email) {
      delete payload.email;
    }

    if (!payload.booking_link) {
      delete payload.booking_link;
    }

    mutate(
      {
        organizationId: teamMember.organization_team_members[0].organization.id,
        schedulingMethod: payload,
      },
      {
        onError: (error: Error) => {
          toast.error(
            "There was an issue saving your scheduling options. Please try again later.",
          );
          onError?.(error);
        },
        onSuccess,
      },
    );

    return data;
  };

  const confirmHandler: SubmitHandler<SchedulingMethod> = (data) => {
    const payload = { ...data };

    if (
      !payload.phone_number?.number &&
      !payload.email &&
      !payload.booking_link
    ) {
      setConfirm(true);
    } else {
      submitHandler(data);
    }
  };

  const hasPhoneNumber = !!watch("phone_number.number");
  const radioClassName = twMerge(
    "disabled:bg-gray-200 disabled:border-none",
    errors.phone_number?.variant && "border-error",
  );

  return (
    <form noValidate onSubmit={handleSubmit(submitHandler)}>
      <fieldset className="grid grid-cols-[1fr_2fr_100px] items-start gap-x-2 gap-y-3 py-5">
        <div className="pt-3">
          <label className="font-medium" htmlFor="input-phone-number">
            Phone number
          </label>
        </div>
        <div>
          <Input
            error={errors.phone_number?.number?.message}
            id="input-phone-number"
            placeholder="Enter phone number for scheduling"
            type="tel"
            {...formatPhoneNumberField("phone_number.number", register)}
          />
        </div>
        <div className="flex items-center gap-x-2">
          <label htmlFor="input-phone-ext">ext.</label>
          <Input
            disabled={!hasPhoneNumber}
            inputClassName="disabled:text-gray-200"
            error={errors.phone_number?.ext?.message}
            id="input-phone-ext"
            placeholder=""
            type="text"
            {...register("phone_number.ext")}
          />
        </div>

        <div className="col-span-2 col-start-2">
          <div className="flex gap-3">
            <div className="flex items-center gap-1">
              <input
                disabled={!hasPhoneNumber}
                id="input-phone-variant-voice"
                className={radioClassName}
                type="radio"
                value={SCHEDULING_METHOD_VARIANTS.VOICE_ONLY}
                {...register("phone_number.variant")}
              />
              <label
                className="font-normal"
                htmlFor="input-phone-variant-voice"
              >
                {
                  SCHEDULING_METHOD_VARIANT_LABELS[
                    SCHEDULING_METHOD_VARIANTS.VOICE_ONLY
                  ]
                }
              </label>
            </div>

            <div className="flex items-center gap-1">
              <input
                disabled={!hasPhoneNumber}
                id="input-phone-variant-text"
                className={radioClassName}
                type="radio"
                value={SCHEDULING_METHOD_VARIANTS.TEXT_ONLY}
                {...register("phone_number.variant")}
              />
              <label htmlFor="input-phone-variant-text">
                {
                  SCHEDULING_METHOD_VARIANT_LABELS[
                    SCHEDULING_METHOD_VARIANTS.TEXT_ONLY
                  ]
                }
              </label>
            </div>

            <div className="flex items-center gap-1">
              <input
                disabled={!hasPhoneNumber}
                id="input-phone-variant-voice-text"
                className={radioClassName}
                type="radio"
                value={SCHEDULING_METHOD_VARIANTS.VOICE_AND_TEXT}
                {...register("phone_number.variant")}
              />
              <label htmlFor="input-phone-variant-voice-text">
                {
                  SCHEDULING_METHOD_VARIANT_LABELS[
                    SCHEDULING_METHOD_VARIANTS.VOICE_AND_TEXT
                  ]
                }
              </label>
            </div>
          </div>
          {errors.phone_number?.variant && (
            <ErrorText className="pt-1 font-light">
              {errors.phone_number.variant?.message}
            </ErrorText>
          )}
        </div>
      </fieldset>

      <fieldset className="grid grid-cols-[1fr_2fr_100px] items-center gap-2 border-t border-gray-300 py-5">
        <div>
          <label className="font-medium" htmlFor="input-email">
            Email
          </label>
        </div>

        <div className="flex-1">
          <Input
            error={errors.email?.message}
            id="input-email"
            placeholder="Enter email address for scheduling"
            type="email"
            {...register("email")}
          />
        </div>
      </fieldset>

      <fieldset className="grid grid-cols-[1fr_2fr_100px] items-center gap-2 border-t border-gray-300 py-5">
        <div>
          <label className="font-medium" htmlFor="input-calendar-link">
            Booking link
          </label>
        </div>
        <div>
          <Input
            error={errors.booking_link?.message}
            id="input-calendar-link"
            placeholder="Enter URL for booking link"
            type="text"
            {...register("booking_link")}
          />
        </div>
      </fieldset>

      <div className="pt-5">
        <CancelSubmitButtons
          submitButtonLabel="Save changes"
          cancelClassName="flex-1"
          submitClassName="flex-1"
          handleCancel={onCancel}
          isSubmitting={isPending}
          containerClassName="w-1/2 ml-auto"
          disableSubmit={!isDirty}
          handleSubmit={handleSubmit(confirmHandler)}
        />
      </div>

      <ConfirmActionModal
        title="Confirm removal"
        description="Are you sure you want to remove all scheduling methods? Scheduling will be turned off for all consultation links."
        show={confirm}
        confirmButtonText="Remove"
        onClose={() => setConfirm(false)}
        cancelButtonClassName="hidden"
        onConfirm={handleSubmit(submitHandler)}
      />
    </form>
  );
};
